import React, { useEffect, useState } from "react";
import {
  StudentsPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
  ButtonWrapper,
  TitleWrapper,
} from "./StudentsPage.styles";
import Sidebar from "../../components/Sidebar";
import Table, { TableLink, TableText } from "../../components/Table";
import {
  createStudent,
  fetchStudents,
  updateAccess,
  updateNote,
} from "../../services/studentService/studentService";
import { StudentPurchase } from "../../services/studentService/studentService.types";
import Button from "../../components/Buttons/Button";
import { CreateModal } from "../../components/Modals";
import { Course } from "../../services/leadService/leadService.types";
import { fetchCourses, fetchUserId } from "../../services/leadService/leadService";
import TableAction from "../../components/TableAction";
import TableMetrics from "../../components/TableMetrics";
import StatusSelect from "../../components/StatusSelect";
import type { StatusSelectOption } from "../../components/StatusSelect";
import { formatServerErrors } from "../../utils/errorHandler";
import LeadNoteModal from "../LeadsPage/LeadNoteModal";
import StudentDocumentModal from "./StudentDocumentModal";

import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { useListPage } from "hooks/useListPage";
import { getServerErrorData } from "utils/httpError";

const courseAccessOptions: StatusSelectOption[] = [
  { label: "Доступен", value: "Доступен", tone: "active" },
  { label: "Не доступен", value: "Не доступен", tone: "inactive" },
];

const StudentsPage: React.FC = () => {
  const [studentCount, setStudentCount] = useState<number>(0);
  const [courseCount, setCourseCount] = useState<number>(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const [courses, setCourses] = useState<Course[]>([]);
  const [noteModalStudent, setNoteModalStudent] = useState<StudentPurchase | null>(null);
  const [documentStudent, setDocumentStudent] = useState<StudentPurchase | null>(null);
  const [isNoteSaving, setIsNoteSaving] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [studentFilterOptions, setStudentFilterOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [courseFilterOptions, setCourseFilterOptions] = useState<Array<{ label: string; value: string }>>([]);
  const filterKey = JSON.stringify(filters);
  const toast = useNotify();

  const {
    items: students,
    setItems: setStudents,
    loading,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    handleSort,
    reload: loadStudents,
  } = useListPage<StudentPurchase, Awaited<ReturnType<typeof fetchStudents>>>({
    fetcher: ({ page, pageSize: size, orderBy }) =>
      fetchStudents(page, size, orderBy, {
        studentIds: filters.fullName ?? [],
        courseIds: filters.courseName ?? [],
      }),
    select: (response) => ({ items: response.results.results, total: response.count }),
    onLoaded: (response) => {
      setStudentCount(response.results.aggregated_data.student_count);
      setCourseCount(response.results.aggregated_data.course_count);
      setStudentFilterOptions(response.results.filter_options?.students ?? []);
      setCourseFilterOptions(response.results.filter_options?.courses ?? []);
    },
    errorMessage: "Ошибка загрузки учеников",
    errorStateMessage: "Не удалось загрузить список учеников.",
    reloadKey: filterKey,
  });

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const userId = await fetchUserId();
        const coursesData = await fetchCourses({ userId });
        setCourses(coursesData.map((course) => ({ id: Number(course.id), title: course.title })));
      } catch (error) {
        logger.error("Ошибка при загрузке курсов:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось загрузить курсы", life: 3000 });
      }
    };
    void loadCourses();
  }, [toast]);

  const handleFilterSave = (key: string, selected: string[]) => {
    setFilters((previous) => ({ ...previous, [key]: selected }));
    setCurrentPage(1);
    setActiveFilter(null);
  };

  const handleFilterReset = (key: string) => {
    setFilters((previous) => {
      const next = { ...previous };
      delete next[key];
      return next;
    });
    setCurrentPage(1);
    setActiveFilter(null);
  };

  const handleCourseAccessChange = async (paymentId: number, access: string) => {
    try {
      const response = await updateAccess(paymentId, access === "Доступен");
      setStudents((prevStudents) =>
        prevStudents.map((student) =>
          student.payment_id === paymentId
            ? { ...student, course_access: response.course_access }
            : student,
        ),
      );
      toast.show({ severity: "success", summary: "Успешно", detail: "Доступ успешно обновлен", life: 3000 });
    } catch (error) {
      logger.error("Ошибка при обновлении доступа:", error);
      toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось обновить доступ", life: 3000 });
    }
  };

  const persistStudentNote = async (note: string) => {
    if (!noteModalStudent) return;
    setIsNoteSaving(true);
    try {
      const paymentId = noteModalStudent.payment_id;
      await updateNote(paymentId, note);
      setStudents((prevStudents) =>
        prevStudents.map((student) =>
          student.payment_id === paymentId ? { ...student, note } : student,
        ),
      );
      toast.show({
        severity: "success",
        summary: "Успешно",
        detail: note ? "Заметка сохранена" : "Заметка удалена",
        life: 3000,
      });
      setNoteModalStudent(null);
    } catch (error) {
      logger.error("Ошибка при обновлении заметки ученика:", error);
      toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось обновить заметку", life: 3000 });
    } finally {
      setIsNoteSaving(false);
    }
  };

  const headers = [
    {
      label: "ФИО",
      key: "fullName",
      fieldName: "student__full_name",
      isFilter: true,
      filterType: "list" as const,
      listOptions: studentFilterOptions,
      listFilter: {
        title: "ФИО",
        searchable: true,
        searchPlaceholder: "Найти ученика",
        emptyMessage: "Ученики не найдены",
      },
    },
    { label: "Телеграм", key: "telegram", fieldName: "student__user_name" },
    { label: "Телефон", key: "phone", fieldName: "student__phone_number" },
    { label: "Почта", key: "email", fieldName: "student__email" },
    {
      label: "Название курса",
      key: "courseName",
      fieldName: "course__title",
      isFilter: true,
      filterType: "list" as const,
      listOptions: courseFilterOptions,
      listFilter: {
        title: "Курс",
        searchable: true,
        searchPlaceholder: "Найти курс",
        emptyMessage: "Курсы не найдены",
      },
    },
    { label: "Заметка", key: "note", fieldName: "note" },
    { label: "Документ", key: "documents", sortable: false },
    { label: "Действия", key: "actions", fieldName: "" },
    { label: "Доступ к курсу", key: "course_access", fieldName: "course_access" },
    { label: "", key: "placeholder", sortable: false },
  ];

  const rows = students.map((student) => ({
    id: student.payment_id,
    fullName: <TableText weight={500}>{student.student.full_name || "-"}</TableText>,
    telegram: student.student.user_name ? (
      <TableLink href={`https://t.me/${student.student.user_name}`} external>
        @{student.student.user_name}
      </TableLink>
    ) : (
      <TableText tone="muted">Не привязан</TableText>
    ),
    phone: <TableText tone="muted" numeric>{student.student.phone_number || "-"}</TableText>,
    email: <TableText tone="muted" truncate maxWidth="190px">{student.student.email || "-"}</TableText>,
    courseName: <TableText truncate maxWidth="190px">{student.course.title}</TableText>,
    note: (
      <TableAction onClick={() => setNoteModalStudent(student)} ariaLabel={`Открыть заметку ученика ${student.student.full_name}`}>
        Смотреть
      </TableAction>
    ),
    documents: (
      <TableAction
        onClick={() => setDocumentStudent(student)}
        ariaLabel={`Управлять документом ученика ${student.student.full_name} по курсу ${student.course.title}`}
      >
        Управлять
      </TableAction>
    ),
    actions: (
      <TableAction
        href={`/students/${student.student.id}/courses/${student.course.id}/logs`}
        queryParams={{ studentName: student.student.full_name, courseName: student.course.title }}
        disabled={!student.has_activity}
      >
        {student.has_activity ? "Смотреть активность" : "Не было активностей"}
      </TableAction>
    ),
    course_access: (
      <StatusSelect
        options={courseAccessOptions}
        value={student.course_access ? "Доступен" : "Не доступен"}
        onChange={(selectedValue) => handleCourseAccessChange(student.payment_id, String(selectedValue))}
        ariaLabel={`Доступ к курсу для ${student.student.full_name}`}
      />
    ),
    placeholder: null,
  }));

  const rowAmountOptions = [
    { label: "7 учеников", value: 7 },
    { label: "15 учеников", value: 15 },
    { label: "20 учеников", value: 20 },
  ];

  const handleCreateStudent = async (formData: Record<string, string | File[] | null>) => {
    setExternalErrors({});
    try {
      const payload = {
        surname: formData.surname as string,
        name: formData.name as string,
        patronymic: formData.patronymic as string | undefined,
        email: formData.email as string,
        telegramUsername: formData.telegramUsername as string | undefined,
        tg_id: formData.tg_id as string,
        phone: formData.phone as string,
        course: formData.course as string,
        note: formData.note as string | undefined,
      };
      const response = await createStudent(payload);
      toast.show({ severity: "success", summary: "Успешно", detail: response.message, life: 3000 });
      setIsCreateModalOpen(false);
      loadStudents();
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formattedErrors = formatServerErrors(serverErrors);
        setExternalErrors(formattedErrors);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: formattedErrors.detail || formattedErrors.error || formattedErrors.non_field_errors || "Произошла ошибка при создании студента.",
          life: 3000,
        });
      } else {
        logger.error("Ошибка при создании ученика:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Произошла ошибка при создании студента.", life: 3000 });
      }
    }
  };

  return (
    <StudentsPageContainer>
      <Sidebar />
      <ContentWrapper>
        <HeaderContainer>
          <TitleWrapper>
            <Title>Список учеников</Title>
            <ButtonWrapper>
              <Button onClick={() => setIsCreateModalOpen(true)} type="button" variant="primary" size="lg">
                Добавить ученика
              </Button>
            </ButtonWrapper>
          </TitleWrapper>
          <TableMetrics
            items={[
              { label: "Всего учеников", value: studentCount },
              { label: "Куплено курсов", value: courseCount },
            ]}
          />
        </HeaderContainer>

        <Table
          emptyState={{ title: "Учеников пока нет", description: "Сюда попадут все, кто записался на ваши курсы." }}
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          onSort={handleSort}
          rowAmountOptions={rowAmountOptions}
          minHeight="210px"
          isExpanded
          loading={loading}
          activeFilter={activeFilter}
          handleActiveFilterChange={setActiveFilter}
          appliedFilters={filters}
          onFilterSave={handleFilterSave}
          onFilterReset={handleFilterReset}
        />

        {isCreateModalOpen && (
          <CreateModal
            title="Добавление ученика"
            fields={[
              { name: "surname", type: "text", placeholder: "Фамилия", required: true },
              { name: "name", type: "text", placeholder: "Имя", required: true },
              { name: "patronymic", type: "text", placeholder: "Отчество", required: false },
              { name: "email", type: "email", placeholder: "Почта", required: true },
              { name: "telegramUsername", type: "text", placeholder: "Имя в телеграм", required: false },
              { name: "tg_id", type: "text", placeholder: "Телеграм ID", required: true },
              { name: "phone", type: "text", placeholder: "Номер телефона", required: true },
              {
                name: "course",
                type: "selectWithInput",
                placeholder: "Курс",
                options: courses.map((course) => ({ label: course.title, value: String(course.id) })),
                required: true,
              },
              { name: "note", type: "textarea", placeholder: "Заметки", required: false },
            ]}
            onSubmit={handleCreateStudent}
            submitButtonText="Сохранить"
            onCancel={() => setIsCreateModalOpen(false)}
            hasDragAndDrop={false}
            externalErrors={externalErrors}
          />
        )}
      </ContentWrapper>

      <LeadNoteModal
        isOpen={noteModalStudent !== null}
        initialNote={noteModalStudent?.note || ""}
        isSaving={isNoteSaving}
        onClose={() => {
          if (!isNoteSaving) setNoteModalStudent(null);
        }}
        onSave={persistStudentNote}
        onDelete={() => persistStudentNote("")}
      />

      {documentStudent && (
        <StudentDocumentModal purchase={documentStudent} onClose={() => setDocumentStudent(null)} />
      )}
    </StudentsPageContainer>
  );
};

export default StudentsPage;
